/** UserData `name` key used to persist the selected avatar color for the current user. */
export const AVATAR_COLOR_USER_DATA_NAME = "avatar_color";

/** A selectable avatar background color, keyed to the shared data-viz palette. */
export type AvatarColorKey =
  | "indigo"
  | "purple"
  | "pink"
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "grey";

interface AvatarColorOption {
  key: AvatarColorKey;
  label: string;
  bgClassName: string;
  textClassName: string;
}

/** The 9 base data-viz colors, using the "soft" tokens also used for tag chips (see stacklet-ui's tagVariants.ts). */
export const AVATAR_COLORS: AvatarColorOption[] = [
  {
    key: "indigo",
    label: "Indigo",
    bgClassName: "bg-data-viz-soft-indigo",
    textClassName: "text-data-viz-indigo-900",
  },
  {
    key: "purple",
    label: "Purple",
    bgClassName: "bg-data-viz-soft-violet",
    textClassName: "text-data-viz-violet-900",
  },
  {
    key: "pink",
    label: "Pink",
    bgClassName: "bg-data-viz-soft-pink",
    textClassName: "text-data-viz-pink-900",
  },
  {
    key: "red",
    label: "Red",
    bgClassName: "bg-data-viz-soft-red",
    textClassName: "text-data-viz-red-900",
  },
  {
    key: "orange",
    label: "Orange",
    bgClassName: "bg-data-viz-soft-orange",
    textClassName: "text-stacklet-orange-900",
  },
  {
    key: "yellow",
    label: "Yellow",
    bgClassName: "bg-data-viz-soft-yellow",
    textClassName: "text-data-viz-yellow-900",
  },
  {
    key: "green",
    label: "Green",
    bgClassName: "bg-data-viz-soft-green",
    textClassName: "text-data-viz-green-900",
  },
  {
    key: "blue",
    label: "Blue",
    bgClassName: "bg-data-viz-soft-blue",
    textClassName: "text-data-viz-blue-900",
  },
  {
    key: "grey",
    label: "Grey",
    bgClassName: "bg-data-viz-soft-grey",
    textClassName: "text-text-tag",
  },
];

const AVATAR_COLORS_BY_KEY: Record<AvatarColorKey, AvatarColorOption> =
  Object.fromEntries(
    AVATAR_COLORS.map((option) => [option.key, option]),
  ) as Record<AvatarColorKey, AvatarColorOption>;

export function isAvatarColorKey(value: string): value is AvatarColorKey {
  return value in AVATAR_COLORS_BY_KEY;
}

export function getAvatarColorOption(key: AvatarColorKey): AvatarColorOption {
  return AVATAR_COLORS_BY_KEY[key];
}

/** Sentinel value for "use my gravatar photo" instead of a flat color. */
export const GRAVATAR_KEY = "gravatar" as const;

/** Everything selectable in the avatar picker: the 9 flat colors, or the user's gravatar photo. */
export type AvatarSelectionKey = AvatarColorKey | typeof GRAVATAR_KEY;

export function isAvatarSelectionKey(
  value: string,
): value is AvatarSelectionKey {
  return value === GRAVATAR_KEY || isAvatarColorKey(value);
}
